import {useCallback, useEffect, useState} from 'react';
import {saveDeviceSettings, subscribeToDeviceSettings} from '../services/deviceSettingsApi';
import {useAsyncAction} from './useAsyncAction';

/**
 * Encapsulates CT / device-variable subscription, dirty-tracking, and saving.
 *
 * @param {string} edgeId
 */
export const useDeviceSettings = (edgeId) => {
  const {loading, error, run} = useAsyncAction();

  const [ctType, setCtType] = useState('YHDC_SCT013');
  const [ctPrimaryCurrent, setCtPrimaryCurrent] = useState('100');
  const [ctInvert, setCtInvert] = useState(['0', '0', '0']);

  const [originalCtType, setOriginalCtType] = useState('');
  const [originalCtPrimaryCurrent, setOriginalCtPrimaryCurrent] = useState('');
  const [originalCtInvert, setOriginalCtInvert] = useState([undefined, undefined, undefined]);

  // ─── Subscribe to real-time updates ────────────────────────────────────────

  useEffect(() => {
    if (!edgeId) return;
    const unsubscribe = subscribeToDeviceSettings(
      edgeId,
      (data) => {
        const ctInv = ['0', '0', '0'];
        if (data.SMARTPI_CTTYPE) {
          setCtType(data.SMARTPI_CTTYPE);
          setOriginalCtType(data.SMARTPI_CTTYPE);
        }
        if (data.SMARTPI_PRIMARYCURRENT) {
          setCtPrimaryCurrent(data.SMARTPI_PRIMARYCURRENT);
          setOriginalCtPrimaryCurrent(data.SMARTPI_PRIMARYCURRENT);
        }
        if (data.SMARTPI_CHANGECURRENTDIRECTION_1) ctInv[0] = data.SMARTPI_CHANGECURRENTDIRECTION_1;
        if (data.SMARTPI_CHANGECURRENTDIRECTION_2) ctInv[1] = data.SMARTPI_CHANGECURRENTDIRECTION_2;
        if (data.SMARTPI_CHANGECURRENTDIRECTION_3) ctInv[2] = data.SMARTPI_CHANGECURRENTDIRECTION_3;
        setCtInvert(ctInv);
        setOriginalCtInvert(ctInv);
      },
      (err) => console.error('Device settings listener failed:', err),
    );
    return () => unsubscribe();
  }, [edgeId]);

  // ─── Dirty tracking ───────────────────────────────────────────────────────

  const compareArrays = (a, b) => {
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const hasChanges =
    ctType !== originalCtType ||
    ctPrimaryCurrent !== originalCtPrimaryCurrent ||
    !compareArrays(ctInvert, originalCtInvert);

  // ─── Mutations ─────────────────────────────────────────────────────────────

  const handleSwitchChange = useCallback((index) => {
    setCtInvert((prev) => {
      const next = [...prev];
      next[index] = next[index] === '1' ? '0' : '1';
      return next;
    });
  }, []);

  const save = useCallback(async () => {
    let primaryCurrentToSave = ctPrimaryCurrent;
    if (ctType === 'YHDC_SCT013') {
      primaryCurrentToSave = '100';
      setCtPrimaryCurrent('100');
    }
    await run(
      () =>
        saveDeviceSettings(edgeId, {
          SMARTPI_CTTYPE: ctType,
          SMARTPI_PRIMARYCURRENT: primaryCurrentToSave,
          SMARTPI_CHANGECURRENTDIRECTION_1: ctInvert[0],
          SMARTPI_CHANGECURRENTDIRECTION_2: ctInvert[1],
          SMARTPI_CHANGECURRENTDIRECTION_3: ctInvert[2],
        }),
      'Unable to save CT settings.',
    );
  }, [edgeId, ctType, ctPrimaryCurrent, ctInvert, run]);

  return {
    loading,
    error,
    ctType,
    setCtType,
    ctPrimaryCurrent,
    setCtPrimaryCurrent,
    ctInvert,
    handleSwitchChange,
    hasChanges,
    originalCtType,
    save,
  };
};

